// store.js
import { writable } from 'svelte/store';

export const currentPage = writable('home');
export const formDataStore = writable({});
export const recommendationsStore = writable([]);
export const regimenStore = writable({ morning: [], night: [] });
export const currentRoutineStore = writable([]);

function createUserStore() {
  function normalizeUsername(username) {
      if (username === undefined) {
          return undefined;
      }

      if (username === null) {
          return null;
      }

      const value = String(username).trim();
      const normalized = value.toLowerCase();

      if (!value || normalized === 'null' || normalized === 'there') {
          return null;
      }

      return value;
  }

  // `userId` and `isAuthenticated` are deliberately independent.
  //
  // Anonymous visitors get a generated userId so their analysis can be stored
  // and later migrated onto a real account. That is NOT the same as being
  // signed in. Deriving isAuthenticated from `!!userId` meant anyone who took
  // a selfie was shown "Account | Log Out" and a "My Profile" CTA despite
  // never having signed in — and then saw "Sign In" again after a reload,
  // because the store initialises isAuthenticated to false. Two contradictory
  // answers about who you are, in one session.
  //
  // Only a successful /api/is_authenticated response may set isAuthenticated.
  const { subscribe, set, update } = writable({
      isAuthenticated: false,
      userId: localStorage.getItem('UserId') || null,
      username: null
  });

  return {
      subscribe,

      // Attach an identity to this browser. Says nothing about authentication.
      setUserId: (id) => {
          if (id) {
              localStorage.setItem('UserId', id);
          } else {
              localStorage.removeItem('UserId');
          }
          update(state => ({ ...state, userId: id || null }));
      },

      // No session, but keep the local identity so the visitor's work survives
      // until they sign in.
      signOut: () => {
          update(state => ({ ...state, isAuthenticated: false, username: null }));
      },

      // Full sign-out: drop the local identity too.
      logout: () => {
          localStorage.removeItem('UserId');
          set({ isAuthenticated: false, userId: null, username: null });
      },
      set: (value) => {
          const hasUsername = Object.prototype.hasOwnProperty.call(value, 'username');
          const normalizedUsername = hasUsername ? normalizeUsername(value.username) : undefined;

          if (value.userId) {
              localStorage.setItem('UserId', value.userId);
          } else if (value.userId === null) {
              localStorage.removeItem('UserId');
          }

          update(state => ({
              ...state,
              ...value,
              userId: value.userId === undefined ? state.userId : value.userId,
              username: hasUsername ? normalizedUsername : normalizeUsername(state.username)
          }));
      }
  };
}

export const userStore = createUserStore();
